import { useMemo, useState } from "react";
import { FolderOpen, ChevronRight, Trash2, FileDown } from "lucide-react";

const isPaid = (p) => (p.payment_status || p.status) === "paid";

function toCsv(rows, fmtDate) {
  const head = ["Date", "Email", "Plan", "Amount (USD)", "Status", "Session ID"];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [head.join(",")];
  rows.forEach((p) => {
    lines.push([
      fmtDate(p.created_at),
      p.email || "",
      p.lookup_key || "",
      p.amount || 0,
      p.payment_status || p.status || "",
      p.session_id || p.id || "",
    ].map(esc).join(","));
  });
  return lines.join("\n");
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 2000);
}

export default function AdminPaymentsFolders({ payments, selectedPayments, togglePayment, deletePayment, deleteFolder, fmtDate }) {
  const groups = useMemo(() => {
    const acc = {};
    (payments || []).forEach((p) => {
      const key = (p.email || "Unassigned").toLowerCase();
      (acc[key] ||= { email: p.email || "Unassigned", rows: [] }).rows.push(p);
    });
    return Object.values(acc).sort((a, b) => b.rows.length - a.rows.length);
  }, [payments]);

  const [open, setOpen] = useState({});
  const toggle = (email) => setOpen((o) => ({ ...o, [email]: !o[email] }));

  if (!payments || payments.length === 0) {
    return (
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white py-8 text-center text-[#1e3d3a]/40">
        No payments yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="payments-folders">
      {groups.map((g) => {
        const total = g.rows.filter(isPaid).reduce((s, p) => s + (p.amount || 0), 0);
        const isOpen = !!open[g.email];
        const safe = g.email.replace(/[^A-Za-z0-9_-]+/g, "_");
        return (
          <div key={g.email} data-testid={`payment-folder-${safe}`} className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4">
              <button
                type="button"
                data-testid={`payment-folder-toggle-${safe}`}
                onClick={() => toggle(g.email)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
              >
                <ChevronRight className={`h-4 w-4 text-[#a3823f] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-90" : ""}`} />
                <FolderOpen className="h-5 w-5 text-[#c9a961] shrink-0" />
                <span className="font-medium text-[#1e3d3a] truncate">{g.email}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 shrink-0">
                  {g.rows.length} txn{g.rows.length === 1 ? "" : "s"}
                </span>
              </button>
              <span className="text-sm tabular-nums text-[#1e3d3a]/70 shrink-0">${total.toLocaleString()} paid</span>
              <button
                type="button"
                data-testid={`payment-folder-csv-${safe}`}
                onClick={() => downloadCsv(`Elevare_Payments_${safe}.csv`, toCsv(g.rows, fmtDate))}
                title="Download this client's transactions (CSV)"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
              >
                <FileDown className="h-3.5 w-3.5" /> CSV
              </button>
              {deleteFolder && (
                <button
                  type="button"
                  data-testid={`payment-folder-delete-${safe}`}
                  onClick={() => deleteFolder(g)}
                  title="Remove this client's entire payment folder (does not refund)"
                  className="shrink-0 inline-flex items-center justify-center rounded-full border border-[#1e3d3a]/15 bg-white h-8 w-8 text-[#1e3d3a]/40 hover:border-[#b23a48]/40 hover:bg-[#b23a48]/8 hover:text-[#b23a48] transition-colors duration-300"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              )}
            </div>

            {isOpen && (
              <div className="border-t border-[#1e3d3a]/8 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
                    <tr>
                      <th className="w-8 px-4 py-2.5" />
                      <th className="text-left px-5 py-2.5">Date</th>
                      <th className="text-left px-5 py-2.5">Plan</th>
                      <th className="text-left px-5 py-2.5">Amount</th>
                      <th className="text-left px-5 py-2.5">Status</th>
                      <th className="w-16 px-5 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((p, i) => {
                      const key = p.session_id || p.id;
                      const isSel = key && selectedPayments.has(key);
                      return (
                        <tr key={key || i} className={`border-t border-[#1e3d3a]/8 group ${isSel ? "bg-[#c9a961]/8" : ""}`}>
                          <td className="px-4 py-3">
                            {key && (
                              <input
                                type="checkbox"
                                data-testid={`payment-select-${key}`}
                                checked={isSel}
                                onChange={() => togglePayment(key)}
                                className="h-3.5 w-3.5 accent-[#a3823f] cursor-pointer"
                              />
                            )}
                          </td>
                          <td className="px-5 py-3 text-[#1e3d3a]/70 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                          <td className="px-5 py-3 text-[#1e3d3a]/70">{p.lookup_key || "—"}</td>
                          <td className="px-5 py-3 tabular-nums">${(p.amount || 0).toLocaleString()}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-block text-[10px] uppercase tracking-[0.2em] rounded-full px-2 py-1 ${
                              isPaid(p) ? "bg-[#6b7c3a]/10 text-[#6b7c3a]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"
                            }`}>{p.payment_status || p.status || "—"}</span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <button
                              type="button"
                              data-testid={`payment-delete-${key || i}`}
                              onClick={() => deletePayment(p)}
                              title="Remove from ledger (does not refund)"
                              className="text-[#1e3d3a]/30 hover:text-[#b23a48] transition-colors duration-300 opacity-0 group-hover:opacity-100 focus:opacity-100"
                            >
                              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
